import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import universityService from '../services/universityService';
import boardService from '../services/boardService';
import mockTestService from '../services/mocktestService';
import Advertisement from '../components/common/Advertisement';
import AdBanner from '../components/common/AdBanner';
import SEO from '../components/common/SEO';
import CardCarousel from '../components/common/CardCarousel';
import HeroSlider from '../components/common/HeroSlider';
import rahulTiwariPhoto from '../assets/students-reviews/rahul-tiwari.png';
import kunalSinghPhoto from '../assets/students-reviews/kunal-singh.png';
import priyaRajPhoto from '../assets/students-reviews/priya-raj.png';
import amanDasPhoto from '../assets/students-reviews/aman-das.png';
import anjaliJhaPhoto from '../assets/students-reviews/anjali-jha.png';
import sakshiKumariPhoto from '../assets/students-reviews/sakshi-kumari.png';
import riyaKumariPhoto from '../assets/students-reviews/riya-kumari.png';

// Literal Tailwind gradient classes, one per stat card, cycled via index —
// kept as whole literal strings (not built from a template) so the JIT
// scanner picks up every one regardless of which index lands on which card.
const STAT_CARD_CLASSES = [
    'bg-gradient-to-br from-indigo-500 to-indigo-700',
    'bg-gradient-to-br from-purple-500 to-purple-700',
    'bg-gradient-to-br from-pink-500 to-pink-700',
    'bg-gradient-to-br from-orange-500 to-orange-700',
    'bg-gradient-to-br from-teal-500 to-teal-700',
    'bg-gradient-to-br from-blue-500 to-blue-700',
];

const Home = () => {

    // Pagination states
    const [admitCards, setAdmitCards] = useState([]);
    const [results, setResults] = useState([]);
    const [latestJobs, setLatestJobs] = useState([]);
    
    const [admitPage, setAdmitPage] = useState(1);
    const [resultPage, setResultPage] = useState(1);
    const [jobPage, setJobPage] = useState(1);
    
    const [admitTotalPages, setAdmitTotalPages] = useState(1);
    const [resultTotalPages, setResultTotalPages] = useState(1);
    const [jobTotalPages, setJobTotalPages] = useState(1);
    
    const [loadingNotifications, setLoadingNotifications] = useState(false);
    const [activeTab, setActiveTab] = useState('admit');

    // Phase 6 Step 6A — 3 new homepage sections, all public/dynamic.
    const [universities, setUniversities] = useState([]);
    const [loadingUniversities, setLoadingUniversities] = useState(true);
    const [mockCategories, setMockCategories] = useState([]);
    const [loadingMockCategories, setLoadingMockCategories] = useState(true);
    const [boardCategories, setBoardCategories] = useState([]);
    const [loadingBoardCategories, setLoadingBoardCategories] = useState(true);
    
    // Modal state for notification details
    const [selectedNotification, setSelectedNotification] = useState(null);
    const [showModal, setShowModal] = useState(false);
    
    // Testimonial carousel state
    const [currentTestimonial, setCurrentTestimonial] = useState(0);
    const [testimonialInterval, setTestimonialInterval] = useState(null);

    // Real client-supplied reviews with real photos (assets/students-reviews/).
    const toppers = [
        {
            name: 'Rahul Tiwari',
            role: 'BPSC Aspirant',
            content: 'The mock tests helped me to understand my actual level of preparation. The questions are well designed, and regular practice improved my speed, accuracy, and time management.',
            rating: 5,
            photo: rahulTiwariPhoto
        },
        {
            name: 'Kunal Singh',
            role: 'BRABU',
            content: 'The study material is written in simple and understandable language. Even difficult topics become easier to revise. It is very helpful for systematic preparation.',
            rating: 5,
            photo: kunalSinghPhoto
        },
        {
            name: 'Priya Raj',
            role: 'Class 12 Student',
            content: 'The Board Practice Sets were extremely useful for my preparation. They helped me to understand the exam pattern and practice important questions before the final examination. My confidence improved a lot.',
            rating: 5,
            photo: priyaRajPhoto
        },
        {
            name: 'Aman Das',
            role: 'NOU',
            content: 'I really liked the way the study material is organized. The important concepts and topics are presented clearly, which makes revision much easier, especially before the exams.',
            rating: 5,
            photo: amanDasPhoto
        },
        {
            name: 'Anjali Jha',
            role: 'Class 10 Student',
            content: 'The Board Practice Sets provided excellent practice before the exams. The questions helped me understand the type and level of questions I could expect in the examination.',
            rating: 5,
            photo: anjaliJhaPhoto
        },
        {
            name: 'Sakshi Kumari',
            role: 'BPSC TRE Aspirant',
            content: "Abha Publication's Mock Tests gave me a real exam like practice experience. Regular tests helped me improve my speed and accuracy and reduced my exam anxiety.",
            rating: 5,
            photo: sakshiKumariPhoto
        },
        {
            name: 'Riya Kumari',
            role: 'LNMU & Bihar SI Aspirant',
            content: 'Having study material and practice questions together made my preparation much more convenient. Abha Publication has been a useful resource throughout my exam preparation.',
            rating: 5,
            photo: riyaKumariPhoto
        }
    ];

    useEffect(() => {
        fetchAdmitCards();
        fetchResults();
        fetchJobs();
        fetchUniversities();
        fetchMockCategories();
        fetchBoardCategories();

        const interval = setInterval(() => {
            setCurrentTestimonial((prev) => (prev + 1) % toppers.length);
        }, 5000);
        setTestimonialInterval(interval);
        
        return () => clearInterval(interval);
    }, []);

    const fetchAdmitCards = async (page = 1) => {
        setLoadingNotifications(true);
        try {
            const res = await api.get(`/notifications/admit-cards?page=${page}&limit=6`);
            setAdmitCards(res.data.notifications || []);
            setAdmitTotalPages(res.data.totalPages || 1);
            setAdmitPage(res.data.page || 1);
        } catch (error) {
            console.error('Error fetching admit cards:', error);
        } finally {
            setLoadingNotifications(false);
        }
    };

    const fetchResults = async (page = 1) => {
        setLoadingNotifications(true);
        try {
            const res = await api.get(`/notifications/results?page=${page}&limit=6`);
            setResults(res.data.notifications || []);
            setResultTotalPages(res.data.totalPages || 1);
            setResultPage(res.data.page || 1);
        } catch (error) {
            console.error('Error fetching results:', error);
        } finally {
            setLoadingNotifications(false);
        }
    };

    const fetchJobs = async (page = 1) => {
        setLoadingNotifications(true);
        try {
            const res = await api.get(`/notifications?type=general&page=${page}&limit=6`);
            setLatestJobs(res.data.notifications || []);
            setJobTotalPages(res.data.totalPages || 1);
            setJobPage(res.data.page || 1);
        } catch (error) {
            console.error('Error fetching jobs:', error);
        } finally {
            setLoadingNotifications(false);
        }
    };

    const fetchUniversities = async () => {
        setLoadingUniversities(true);
        try {
            const data = await universityService.getUniversities();
            setUniversities(data || []);
        } catch (error) {
            console.error('Error fetching universities:', error);
        } finally {
            setLoadingUniversities(false);
        }
    };

    const fetchMockCategories = async () => {
        setLoadingMockCategories(true);
        try {
            const data = await mockTestService.getCategories();
            setMockCategories(data || []);
        } catch (error) {
            console.error('Error fetching gov exam categories:', error);
        } finally {
            setLoadingMockCategories(false);
        }
    };

    const fetchBoardCategories = async () => {
        setLoadingBoardCategories(true);
        try {
            const data = await boardService.getCategories();
            setBoardCategories(data || []);
        } catch (error) {
            console.error('Error fetching board categories:', error);
        } finally {
            setLoadingBoardCategories(false);
        }
    };

    const handleAdmitLoadMore = () => {
        if (admitPage < admitTotalPages) {
            fetchAdmitCards(admitPage + 1);
        }
    };

    const handleResultLoadMore = () => {
        if (resultPage < resultTotalPages) {
            fetchResults(resultPage + 1);
        }
    };

    const handleJobLoadMore = () => {
        if (jobPage < jobTotalPages) {
            fetchJobs(jobPage + 1);
        }
    };

    const handlePrevTestimonial = () => {
        setCurrentTestimonial((prev) => (prev - 1 + toppers.length) % toppers.length);
        resetInterval();
    };

    const handleNextTestimonial = () => {
        setCurrentTestimonial((prev) => (prev + 1) % toppers.length);
        resetInterval();
    };

    const resetInterval = () => {
        if (testimonialInterval) clearInterval(testimonialInterval);
        const interval = setInterval(() => {
            setCurrentTestimonial((prev) => (prev + 1) % toppers.length);
        }, 5000);
        setTestimonialInterval(interval);
    };

    // Open modal with notification details
    const openNotificationModal = (item) => {
        setSelectedNotification(item);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedNotification(null);
    };

    const stats = [
        { label: 'Study Materials', value: '1000+', icon: '📖' },
        { label: 'MCQ Questions', value: '100000+', icon: '❓' },
        { label: 'Mock Test Series', value: '500+', icon: '📝' },
        { label: 'Happy Students', value: '45k+', icon: '👨‍🎓' },
        { label: 'Active Users', value: '20k+', icon: '👥' },
        { label: 'Passing Rate', value: '85%', icon: '🎯' },
    ];

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    const getJobIcon = (title) => {
        if (title?.toLowerCase().includes('railway')) return '🚂';
        if (title?.toLowerCase().includes('bank')) return '🏦';
        if (title?.toLowerCase().includes('ssc')) return '📋';
        if (title?.toLowerCase().includes('upsc')) return '🇮🇳';
        if (title?.toLowerCase().includes('police')) return '👮';
        if (title?.toLowerCase().includes('teacher')) return '👨‍🏫';
        return '📢';
    };

    const renderTabContent = () => {
        if (loadingNotifications) {
            return (
                <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
                </div>
            );
        }

        if (activeTab === 'admit') {
            if (admitCards.length === 0) {
                return (
                    <div className="text-center py-12">
                        <div className="text-6xl mb-4">📄</div>
                        <p className="text-gray-500 text-base">No admit cards available at the moment.</p>
                        <p className="text-sm text-gray-400 mt-2">Check back later for updates.</p>
                    </div>
                );
            }
            return (
                <div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {admitCards.map((item) => (
                            <div
                                key={item._id}
                                onClick={() => openNotificationModal(item)}
                                className="group bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-5 cursor-pointer hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-blue-100"
                            >
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center text-white text-xl group-hover:scale-110 transition-transform flex-shrink-0">
                                        📄
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-bold text-gray-800 text-base md:text-lg">{item.title}</h3>
                                        {item.examName && <p className="text-sm text-gray-500 mt-1">{item.examName}</p>}
                                        {item.shortDescription && (
                                            <p className="text-sm text-gray-600 mt-2 line-clamp-2">{item.shortDescription}</p>
                                        )}
                                        <p className="text-xs text-gray-400 mt-2">📅 {formatDate(item.publishDate)}</p>
                                    </div>
                                </div>
                                <div className="mt-3 flex justify-end">
                                    <span className="text-xs font-medium text-indigo-600 group-hover:underline">View Details →</span>
                                </div>
                            </div>
                        ))}
                    </div>
                    {admitPage < admitTotalPages && (
                        <div className="text-center mt-8">
                            <button onClick={handleAdmitLoadMore} className="px-6 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all text-sm font-semibold shadow-md hover:shadow-lg">
                                Load More
                            </button>
                        </div>
                    )}
                </div>
            );
        }

        if (activeTab === 'result') {
            if (results.length === 0) {
                return (
                    <div className="text-center py-12">
                        <div className="text-6xl mb-4">🏆</div>
                        <p className="text-gray-500 text-base">No results available at the moment.</p>
                        <p className="text-sm text-gray-400 mt-2">Results will appear here once published.</p>
                    </div>
                );
            }
            return (
                <div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {results.map((item) => (
                            <div
                                key={item._id}
                                onClick={() => openNotificationModal(item)}
                                className="group bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-5 cursor-pointer hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-green-100"
                            >
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl flex items-center justify-center text-white text-xl group-hover:scale-110 transition-transform flex-shrink-0">
                                        🏆
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-bold text-gray-800 text-base md:text-lg">{item.title}</h3>
                                        {item.examName && <p className="text-sm text-gray-500 mt-1">{item.examName}</p>}
                                        {item.shortDescription && (
                                            <p className="text-sm text-gray-600 mt-2 line-clamp-2">{item.shortDescription}</p>
                                        )}
                                        <p className="text-xs text-gray-400 mt-2">📅 {formatDate(item.publishDate)}</p>
                                    </div>
                                </div>
                                <div className="mt-3 flex justify-end">
                                    <span className="text-xs font-medium text-green-600 group-hover:underline">View Details →</span>
                                </div>
                            </div>
                        ))}
                    </div>
                    {resultPage < resultTotalPages && (
                        <div className="text-center mt-8">
                            <button onClick={handleResultLoadMore} className="px-6 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all text-sm font-semibold shadow-md hover:shadow-lg">
                                Load More
                            </button>
                        </div>
                    )}
                </div>
            );
        }

        if (activeTab === 'job') {
            if (latestJobs.length === 0) {
                return (
                    <div className="text-center py-12">
                        <div className="text-6xl mb-4">📢</div>
                        <p className="text-gray-500 text-base">No job notifications available at the moment.</p>
                        <p className="text-sm text-gray-400 mt-2">Check back later for updates.</p>
                    </div>
                );
            }
            return (
                <div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {latestJobs.map((item) => (
                            <div
                                key={item._id}
                                onClick={() => openNotificationModal(item)}
                                className="group bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-5 cursor-pointer hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-amber-100"
                            >
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl flex items-center justify-center text-white text-xl group-hover:scale-110 transition-transform flex-shrink-0">
                                        {getJobIcon(item.title)}
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-bold text-gray-800 text-base md:text-lg">{item.title}</h3>
                                        {item.examName && <p className="text-sm text-gray-500 mt-1">{item.examName}</p>}
                                        {item.shortDescription && (
                                            <p className="text-sm text-gray-600 mt-2 line-clamp-2">{item.shortDescription}</p>
                                        )}
                                        <p className="text-xs text-gray-400 mt-2">📅 {formatDate(item.publishDate)}</p>
                                    </div>
                                </div>
                                <div className="mt-3 flex justify-end">
                                    <span className="text-xs font-medium text-amber-600 group-hover:underline">View Details →</span>
                                </div>
                            </div>
                        ))}
                    </div>
                    {jobPage < jobTotalPages && (
                        <div className="text-center mt-8">
                            <button onClick={handleJobLoadMore} className="px-6 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all text-sm font-semibold shadow-md hover:shadow-lg">
                                Load More
                            </button>
                        </div>
                    )}
                </div>
            );
        }

        return null;
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
            <SEO
                title="University Study Material, Mock Tests & Practice Sets"
                description="Abha Publication offers University Study Material, Mock Tests, and Board Practice Sets for Bihar government exams like BPSC, BSSC, and more — trusted by thousands of students across India."
            />
            <Advertisement />
            <AdBanner />

            <HeroSlider />

            {/* Stats Bar */}
            <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-10 sm:py-12">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-5">
                    {stats.map((stat, index) => (
                        <div
                            key={index}
                            className={`rounded-2xl p-4 sm:p-5 text-center shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-300 ${STAT_CARD_CLASSES[index % STAT_CARD_CLASSES.length]}`}
                        >
                            <div className="text-2xl sm:text-3xl mb-2">{stat.icon}</div>
                            <div className="text-xl sm:text-2xl font-extrabold text-white">{stat.value}</div>
                            <div className="text-[11px] sm:text-xs text-white/90 font-medium mt-1">{stat.label}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* University Showcase */}
            <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-14">
                <div className="text-center mb-10">
                    <h2 className="text-2xl md:text-4xl font-extrabold text-gray-800">
                        Explore Our{' '}
                        <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                            University Study Material
                        </span>
                    </h2>
                    <p className="text-gray-500 mt-3 max-w-2xl mx-auto text-base">
                        Access high-quality notes, practice sets, previous year papers and more for your university exams.
                    </p>
                </div>

                {loadingUniversities ? (
                    <div className="flex justify-center py-12">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
                    </div>
                ) : universities.length === 0 ? (
                    <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500">No universities available yet.</div>
                ) : (
                    <CardCarousel color="indigo">
                        {universities.map((uni) => (
                            <Link
                                key={uni._id}
                                to={`/university-study-material/${uni._id}`}
                                className="group bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                            >
                                <div className="h-32 md:h-36 bg-gradient-to-r from-indigo-100 to-purple-100 flex items-center justify-center overflow-hidden">
                                    {uni.thumbnail || uni.logo ? (
                                        <img
                                            src={uni.thumbnail || uni.logo}
                                            alt={uni.name}
                                            className="w-full h-full object-cover"
                                            onError={(e) => { e.target.src = 'https://placehold.co/400x200/indigo/white?text=University'; }}
                                        />
                                    ) : (
                                        <svg className="w-12 h-12 text-indigo-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11m16-11v11M9 21v-6a1 1 0 011-1h4a1 1 0 011 1v6" />
                                        </svg>
                                    )}
                                </div>
                                <div className="p-5">
                                    <h3 className="text-lg font-bold text-gray-800 line-clamp-1">{uni.name}</h3>
                                    <p className="text-sm text-gray-500 mt-1">{uni.city}{uni.state ? `, ${uni.state}` : ''}</p>
                                    <span className="mt-4 inline-flex w-full items-center justify-center gap-1 bg-indigo-600 text-white py-2.5 px-4 rounded-lg font-semibold text-sm group-hover:bg-indigo-700 transition-all">
                                        Explore Now
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                        </svg>
                                    </span>
                                </div>
                            </Link>
                        ))}
                    </CardCarousel>
                )}
            </section>

            {/* 3 Tabs Section */}
            <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-14">
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
                    <div className="flex flex-wrap border-b border-gray-200 bg-gray-50/80">
                        <button onClick={() => setActiveTab('admit')} className={`flex-1 px-5 py-4 text-center font-bold transition-all duration-200 text-base md:text-lg ${activeTab === 'admit' ? 'text-indigo-600 border-b-2 border-indigo-600 bg-white' : 'text-gray-600 hover:text-indigo-500 hover:bg-gray-100'}`}>
                            <span className="flex items-center justify-center gap-2">📄 Admit Cards</span>
                        </button>
                        <button onClick={() => setActiveTab('result')} className={`flex-1 px-5 py-4 text-center font-bold transition-all duration-200 text-base md:text-lg ${activeTab === 'result' ? 'text-indigo-600 border-b-2 border-indigo-600 bg-white' : 'text-gray-600 hover:text-indigo-500 hover:bg-gray-100'}`}>
                            <span className="flex items-center justify-center gap-2">🏆 Results</span>
                        </button>
                        <button onClick={() => setActiveTab('job')} className={`flex-1 px-5 py-4 text-center font-bold transition-all duration-200 text-base md:text-lg ${activeTab === 'job' ? 'text-indigo-600 border-b-2 border-indigo-600 bg-white' : 'text-gray-600 hover:text-indigo-500 hover:bg-gray-100'}`}>
                            <span className="flex items-center justify-center gap-2">📢 Latest Jobs</span>
                        </button>
                    </div>
                    <div className="p-5 md:p-7">{renderTabContent()}</div>
                </div>
            </section>

            {/* Mock Test Series */}
            <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-14">
                <div className="text-center mb-10">
                    <h2 className="text-2xl md:text-4xl font-extrabold text-gray-800">
                        Mock Test Series{' '}
                        <span className="bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                            for Top Government Exams
                        </span>
                    </h2>
                    <p className="text-gray-500 mt-3 max-w-2xl mx-auto text-base">
                        Real exam experience, latest pattern, detailed analysis — improve your rank.
                    </p>
                </div>

                {loadingMockCategories ? (
                    <div className="flex justify-center py-12">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
                    </div>
                ) : mockCategories.length === 0 ? (
                    <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500">No exam categories available yet.</div>
                ) : (
                    <CardCarousel color="orange">
                        {mockCategories.map((cat) => (
                            <Link
                                key={cat._id}
                                to={`/mock-tests-new/${cat._id}`}
                                className="group bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                            >
                                <div className="h-32 md:h-36 bg-gradient-to-r from-orange-100 to-red-100 flex items-center justify-center overflow-hidden">
                                    {cat.thumbnail ? (
                                        <img
                                            src={cat.thumbnail}
                                            alt={cat.title}
                                            className="w-full h-full object-cover"
                                            onError={(e) => { e.target.src = 'https://placehold.co/400x200/orange/white?text=Mock+Test'; }}
                                        />
                                    ) : (
                                        <svg className="w-12 h-12 text-orange-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    )}
                                </div>
                                <div className="p-5">
                                    <span className="inline-block text-xs font-semibold text-orange-600 bg-orange-50 px-2.5 py-1 rounded-full mb-2">
                                        {cat.examName}
                                    </span>
                                    <h3 className="text-lg font-bold text-gray-800 line-clamp-1">{cat.title}</h3>
                                    <span className="mt-4 inline-flex w-full items-center justify-center gap-1 bg-orange-600 text-white py-2.5 px-4 rounded-lg font-semibold text-sm group-hover:bg-orange-700 transition-all">
                                        Start Practicing
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                        </svg>
                                    </span>
                                </div>
                            </Link>
                        ))}
                    </CardCarousel>
                )}
            </section>

            {/* Board Practice Sets */}
            <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-14">
                <div className="text-center mb-10">
                    <h2 className="text-2xl md:text-4xl font-extrabold text-gray-800">
                        Board Practice Sets{' '}
                        <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                            for Classes 9 to 12
                        </span>
                    </h2>
                    <p className="text-gray-500 mt-3 max-w-2xl mx-auto text-base">
                        Chapter-wise, topic-wise & full syllabus practice sets — designed as per latest board exam pattern.
                    </p>
                </div>

                {loadingBoardCategories ? (
                    <div className="flex justify-center py-12">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
                    </div>
                ) : boardCategories.length === 0 ? (
                    <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500">No board practice sets available yet.</div>
                ) : (
                    <CardCarousel color="purple">
                        {boardCategories.map((cat) => (
                            <Link
                                key={cat._id}
                                to={`/board-practice-sets/${cat._id}`}
                                className="group bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                            >
                                <div className="h-32 md:h-36 bg-gradient-to-r from-purple-100 to-pink-100 flex items-center justify-center overflow-hidden">
                                    {cat.thumbnail ? (
                                        <img
                                            src={cat.thumbnail}
                                            alt={cat.title}
                                            className="w-full h-full object-cover"
                                            onError={(e) => { e.target.src = 'https://placehold.co/400x200/purple/white?text=Board'; }}
                                        />
                                    ) : (
                                        <svg className="w-12 h-12 text-purple-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                    )}
                                </div>
                                <div className="p-5">
                                    <span className="inline-block text-xs font-semibold text-purple-600 bg-purple-50 px-2.5 py-1 rounded-full mb-2">
                                        Class {cat.className} · {cat.subject}
                                    </span>
                                    <h3 className="text-lg font-bold text-gray-800 line-clamp-1">{cat.title}</h3>
                                    <div className="mt-2">
                                        {cat.discountPrice ? (
                                            <span>
                                                <span className="text-gray-400 line-through mr-2 text-sm">₹{cat.price}</span>
                                                <span className="text-xl font-extrabold text-indigo-600">₹{cat.discountPrice}</span>
                                            </span>
                                        ) : (
                                            <span className="text-xl font-extrabold text-indigo-600">₹{cat.price}</span>
                                        )}
                                    </div>
                                    <span className="mt-3 inline-flex w-full items-center justify-center gap-1 bg-purple-600 text-white py-2.5 px-4 rounded-lg font-semibold text-sm group-hover:bg-purple-700 transition-all">
                                        Start Practicing
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                        </svg>
                                    </span>
                                </div>
                            </Link>
                        ))}
                    </CardCarousel>
                )}
            </section>

            {/* Testimonials Carousel Section - Premium */}
            <section className="bg-gradient-to-br from-indigo-950 via-purple-900 to-indigo-950 py-20">
                <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
                    <div className="text-center mb-14">
                        <span className="inline-block text-yellow-400 font-bold text-sm uppercase tracking-wider bg-yellow-400/20 px-5 py-2 rounded-full">Success Stories</span>
                        <h2 className="text-3xl md:text-5xl font-extrabold text-white mt-5 mb-4">What Our Students Are Saying</h2>
                        <p className="text-indigo-200 text-base max-w-2xl mx-auto">Real stories from real students who achieved their goals with us.</p>
                    </div>
                    
                    <div className="relative">
                        <div className="overflow-hidden rounded-2xl">
                            <div 
                                className="flex transition-transform duration-700 ease-out"
                                style={{ transform: `translateX(-${currentTestimonial * 100}%)` }}
                            >
                                {toppers.map((topper, index) => (
                                    <div key={index} className="w-full flex-shrink-0 px-4">
                                        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 md:p-10 text-center border border-white/20 shadow-2xl">
                                            <div className="flex justify-center mb-5">
                                                {topper.photo ? (
                                                    <img
                                                        src={topper.photo}
                                                        alt={topper.name}
                                                        className="w-20 h-20 md:w-24 md:h-24 rounded-full object-cover border-4 border-yellow-400 shadow-xl"
                                                        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                                                    />
                                                ) : null}
                                                <div
                                                    className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 items-center justify-center text-white font-extrabold text-3xl border-4 border-yellow-400 shadow-xl"
                                                    style={{ display: topper.photo ? 'none' : 'flex' }}
                                                >
                                                    {topper.name.charAt(0).toUpperCase()}
                                                </div>
                                            </div>
                                            <h3 className="text-2xl md:text-3xl font-extrabold text-white">{topper.name}</h3>
                                            <p className="text-yellow-400 text-sm md:text-base font-semibold mt-1">{topper.role}</p>
                                            <div className="flex justify-center gap-1 my-4">
                                                {[...Array(topper.rating)].map((_, i) => (
                                                    <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                                                        <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
                                                    </svg>
                                                ))}
                                            </div>
                                            <p className="text-gray-200 text-base md:text-lg leading-relaxed max-w-2xl mx-auto italic">"{topper.content}"</p>
                                            <div className="mt-6 flex justify-center">
                                                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-green-300 bg-green-500/20 px-3 py-1 rounded-full">
                                                    ✅ Exam Qualified
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        
                        <button onClick={handlePrevTestimonial} className="absolute left-0 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white p-3 rounded-full transition-all duration-200 -ml-4 backdrop-blur-sm">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <button onClick={handleNextTestimonial} className="absolute right-0 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white p-3 rounded-full transition-all duration-200 -mr-4 backdrop-blur-sm">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                        
                        <div className="flex justify-center gap-2 mt-8">
                            {toppers.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => {
                                        setCurrentTestimonial(index);
                                        resetInterval();
                                    }}
                                    className={`transition-all duration-300 rounded-full ${
                                        currentTestimonial === index
                                            ? 'w-8 h-2 bg-yellow-400'
                                            : 'w-2 h-2 bg-white/40 hover:bg-white/60'
                                    }`}
                                    aria-label={`Go to testimonial ${index + 1}`}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Modal for Notification Details */}
            {showModal && selectedNotification && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={closeModal}>
                    <div className="bg-white rounded-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="sticky top-0 bg-white border-b border-gray-200 p-5 flex justify-between items-center rounded-t-2xl">
                            <h3 className="text-xl font-bold text-gray-800">{selectedNotification.title}</h3>
                            <button onClick={closeModal} className="text-gray-400 hover:text-gray-600 transition-colors">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="p-6 space-y-4">
                            {selectedNotification.examName && (
                                <div className="bg-gray-100 rounded-lg p-3">
                                    <p className="text-sm text-gray-500">Exam</p>
                                    <p className="font-semibold text-gray-800">{selectedNotification.examName}</p>
                                </div>
                            )}
                            {selectedNotification.shortDescription && (
                                <div>
                                    <p className="text-sm text-gray-500 mb-1">Overview</p>
                                    <p className="text-gray-700">{selectedNotification.shortDescription}</p>
                                </div>
                            )}
                            {selectedNotification.detailedDescription && (
                                <div>
                                    <p className="text-sm text-gray-500 mb-1">Complete Details</p>
                                    <p className="text-gray-700 leading-relaxed whitespace-pre-line">{selectedNotification.detailedDescription}</p>
                                </div>
                            )}
                            <div className="pt-4">
                                <a
                                    href={selectedNotification.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-full inline-block text-center bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 rounded-xl font-semibold hover:shadow-lg transition-all duration-200"
                                >
                                    {selectedNotification.linkText || 'View Details'}
                                </a>
                            </div>
                            <p className="text-xs text-gray-400 text-center">Published on {formatDate(selectedNotification.publishDate)}</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Home;